// Waveform SVG generator — ported from legacy index.html getWaveformSvg().
//
// Decodes an ArrayBuffer via OfflineAudioContext, downsamples to a
// duration-proportional bar count (~30 bars/sec, floor 80, ceiling 400),
// and returns a data: URL for a mirrored (center-aligned) SVG waveform.
// Result is cached per buffer reference (and ink colour) in a WeakMap.
//
// Tinte (2026-10-02): die Balken folgen `--brandcolor-text`, der Textfarbe
// der Chips — Weiß auf dunklen Brandfarben, Schwarz auf hellen (branding.ts
// kippt sie nach WCAG-Luminanz, der Admin kann sie auch setzen). Vorher war
// die Wellenform fest halbtransparent weiß und auf hellen Brandfarben kaum
// zu sehen. TTS-Chips, STS-Clips und das SFX-Bett nutzen denselben Generator.

const cache = new WeakMap<ArrayBuffer, Map<string, string>>();

const DEFAULT_INK = '#FFFFFF';

/** Tintenfarbe der Wellenform: `--brandcolor-text` des Dokuments, sonst Weiß. */
export function waveformInk(): string {
  if (typeof document === 'undefined') return DEFAULT_INK;
  const v = getComputedStyle(document.documentElement).getPropertyValue('--brandcolor-text').trim();
  return v || DEFAULT_INK;
}

/** SVG-Markup aus normierten Amplituden (0 … 1), rein. Weiß behält die
 *  gewohnte Deckkraft; dunkle Tinte auf heller Fläche braucht etwas mehr. */
export function waveformMarkup(normalized: number[], ink: string): string {
  const bars = normalized.length;
  // viewBox width scales with bars so the per-rect `barW` stays ≥1px
  // and the SVG path compresses well. Height fixed because the chip
  // stretches via `preserveAspectRatio="none"` and background-size.
  const w = Math.max(200, bars * 2);
  const h = 40;
  const barW = w / Math.max(bars, 1);
  const opacity = ink.trim().toUpperCase() === DEFAULT_INK ? 0.2 : 0.28;
  let path = '';
  normalized.forEach((amp, i) => {
    const barH = amp * h * 0.8;
    const x = i * barW;
    const y = (h - barH) / 2;
    path += `<rect x="${x}" y="${y}" width="${Math.max(barW - 0.5, 0.5)}" height="${barH}" fill="${ink}" fill-opacity="${opacity}"/>`;
  });
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none">${path}</svg>`;
}

export async function getWaveformSvg(
  audioBuffer: ArrayBuffer | undefined | null,
  ink: string = waveformInk(),
): Promise<string | null> {
  if (!audioBuffer) return null;
  const cached = cache.get(audioBuffer)?.get(ink);
  if (cached) return cached;

  try {
    const AC =
      typeof OfflineAudioContext !== 'undefined'
        ? OfflineAudioContext
        : (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext })
            .webkitOfflineAudioContext;
    if (!AC) return null;
    const ac = new AC(1, 1, 44100);
    const decoded = await ac.decodeAudioData(audioBuffer.slice(0));
    const raw = decoded.getChannelData(0);

    // Duration-proportional: 30 bars/sec. Long takes render at higher
    // absolute resolution so the SVG stays crisp when stretched across
    // many pixels; short takes keep a reasonable floor.
    const bars = Math.min(400, Math.max(80, Math.round(decoded.duration * 30)));
    const blockSize = Math.max(1, Math.floor(raw.length / bars));
    const amplitudes: number[] = [];
    for (let i = 0; i < bars; i++) {
      let sum = 0;
      const start = i * blockSize;
      for (let j = start; j < start + blockSize && j < raw.length; j++) {
        sum += Math.abs(raw[j]);
      }
      amplitudes.push(sum / blockSize);
    }

    const max = Math.max(...amplitudes, 0.01);
    const normalized = amplitudes.map((a) => a / max);
    const dataUrl = `data:image/svg+xml,${encodeURIComponent(waveformMarkup(normalized, ink))}`;
    const perInk = cache.get(audioBuffer) ?? new Map<string, string>();
    perInk.set(ink, dataUrl);
    cache.set(audioBuffer, perInk);
    return dataUrl;
  } catch {
    return null;
  }
}
